import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

/** The plan's marketing bullets: add (with suggestions from other plans), drag to reorder, double-click to edit. */
export function FeatureListEditor({
  value,
  onChange,
  allFeatures,
}: {
  value: string[];
  onChange: (features: string[]) => void;
  allFeatures: string[];
}) {
  const [featureInput, setFeatureInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => {
    if (!featureInput.trim()) return [];
    const query = featureInput.toLowerCase();
    return allFeatures.filter((f) => f.toLowerCase().includes(query) && !value.includes(f));
  }, [featureInput, allFeatures, value]);

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && featureInput.trim().length > 0);
    setHighlightedIndex(-1);
  }, [suggestions, featureInput]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionsRef.current) {
      const el = suggestionsRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const selectSuggestion = (feature: string) => {
    onChange([...value, feature]);
    setFeatureInput('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      onChange([...value, featureInput.trim()]);
      setFeatureInput('');
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const reordered = [...value];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onChange(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleEditSave = () => {
    if (editingIndex === null) return;
    const trimmed = editingValue.trim();
    if (trimmed) {
      const updated = [...value];
      updated[editingIndex] = trimmed;
      onChange(updated);
    }
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); handleEditSave(); }
    if (e.key === 'Escape') { setEditingIndex(null); setEditingValue(''); }
  };

  return (
    <div>
      <div className="relative flex gap-2 mb-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            aria-label="Add a feature bullet"
            value={featureInput}
            onChange={(e) => setFeatureInput(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0 && featureInput.trim()) setShowSuggestions(true);
            }}
            onKeyDown={(e) => {
              if (showSuggestions && suggestions.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex((prev) => Math.max(prev - 1, -1));
                } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                  e.preventDefault();
                  selectSuggestion(suggestions[highlightedIndex]);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddFeature();
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              } else if (e.key === 'Enter') {
                e.preventDefault();
                handleAddFeature();
              }
            }}
            className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
            placeholder="Add a feature..."
            autoComplete="off"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-50 left-0 right-0 top-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto"
            >
              {suggestions.map((suggestion, i) => {
                const query = featureInput.toLowerCase();
                const matchStart = suggestion.toLowerCase().indexOf(query);
                const before = suggestion.slice(0, matchStart);
                const match = suggestion.slice(matchStart, matchStart + query.length);
                const after = suggestion.slice(matchStart + query.length);
                return (
                  <button
                    key={suggestion}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectSuggestion(suggestion)}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      i === highlightedIndex ? 'bg-cyan-500/20 text-white' : 'text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {before}<span className="text-cyan-400 font-medium">{match}</span>{after}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleAddFeature}
          aria-label="Add feature"
          className="px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-400"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </div>
      <p className="text-sm text-slate-400 mb-2">Drag to reorder • Double-click to edit</p>
      <div className="flex flex-wrap gap-2">
        {value.map((feature, index) => (
          editingIndex === index ? (
            <input
              key={index}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleEditSave}
              autoFocus
              className="px-2 py-1 bg-slate-900 border border-cyan-500 rounded text-sm text-white focus:outline-none min-w-[80px]"
              style={{ width: `${Math.max(80, editingValue.length * 8 + 16)}px` }}
            />
          ) : (
            <span
              key={index}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
              onDoubleClick={() => { setEditingIndex(index); setEditingValue(value[index]); }}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm cursor-grab active:cursor-grabbing select-none transition-all ${
                dragIndex === index
                  ? 'opacity-40 bg-slate-700 text-slate-300'
                  : dragOverIndex === index
                    ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-500'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {feature}
              <button
                type="button"
                aria-label={`Remove ${feature}`}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                onMouseDown={(e) => e.stopPropagation()}
                className="hover:text-red-400"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </span>
          )
        ))}
      </div>
    </div>
  );
}
